import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Listing, Attribute } from "@/lib/types";
import { format } from "date-fns";
import {
  Clock,
  MapPin,
  Tag,
  Building,
  Ruler,
  Bath,
  Car,
  Square,
  DoorOpen,
  Hourglass,
  Receipt,
  Info,
  Wallet,
  Sofa,
  Check,
  X,
  Layers,
  Hammer,
} from "lucide-react";
import LocationDetailsSection from "@/components/property-view/LocationDetailsSection";

interface PropertyDetailsSectionProps {
  property: Listing;
  attributes: Attribute[];
}

const PropertyDetailsSection = ({
  property,
  attributes = [], // Provide a default empty array
}: PropertyDetailsSectionProps) => {
  // Format date if available, otherwise provide a placeholder
  const formatListingDate = (dateString?: string | Date) => {
    if (!dateString) return "Recently added";

    try {
      const date =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      return format(date, "MMM d, yyyy");
    } catch (e) {
      return "Recently added";
    }
  };

  // Get listing date - safely access properties that might not exist
  const listingDate = formatListingDate(
    property.created_at as unknown as string | Date | undefined
  );

  // Normalize attributes to handle different formats
  const normalizedAttributes = attributes
    .map((attribute) => {
      if (
        typeof attribute === "object" &&
        attribute !== null &&
        "name" in attribute
      ) {
        return {
          id: attribute.id || String(Math.random()),
          name: attribute.name,
        };
      }
      return null;
    })
    .filter((f): f is { id: string | number; name: string } => f !== null);

  return (
    <>
      <Card className="p-5 border border-propertylk-blue/10">
        <h3 className="text-lg font-semibold text-propertylk-darkblue mb-4">
          Property Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-6">
          {/* Listing Type */}
          {property.listing_type != null && (
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-gray-600" />
              <span className="text-gray-600">Listing Type</span>
              <span className="font-medium ml-2">
                {property.listing_type === "rent"
                  ? "For Rent"
                  : property.listing_type === "sale"
                  ? "For Sale"
                  : "For Lease"}
              </span>
            </div>
          )}

          {/* Property Type */}
          {property.propertyType?.name != null &&
            property.propertyType?.name !== "NULL" && (
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Property Type</span>
                <span className="font-medium ml-2">
                  {property.propertyType?.display_name || "Commercial"}
                </span>
              </div>
            )}

          {/*Land Size(Perch) */}
          {property.plot_size != null &&
            (property.plot_size as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Ruler className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Land Size</span>
                <span className="font-medium ml-2">
                  {" "}
                  {property.plot_size}{" "}
                  {property.size_unit !== "NULL" ? property.size_unit : "perch"}
                </span>
              </div>
            )}

          {/*Floor Size */}
          {property.floor_size != null &&
            (property.floor_size as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Square className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Floor Size</span>
                <span className="font-medium ml-2">
                  {property.floor_size}{" "}
                  {property.size_unit !== "NULL" ? property.size_unit : "sq ft"}
                </span>
              </div>
            )}

          {/* Furnishing */}
          {property.furnishing != null && property.furnishing !== "NULL" && (
            <div className="flex items-center gap-2">
              <Sofa className="h-5 w-5 text-gray-600" />
              <span className="text-gray-600">Furnishing</span>
              <span className="font-medium ml-2 capitalize">
                {property.furnishing}
              </span>
            </div>
          )}

          {/* Rooms / Partitions */}
          {property.rooms != null && (property.rooms as any) !== "NULL" && (
            <div className="flex items-center gap-2">
              <DoorOpen className="h-5 w-5 text-gray-600" />
              <span className="text-gray-600">Rooms / Partitions</span>
              <span className="font-medium ml-2">{property.rooms}</span>
            </div>
          )}

          {/* Bathrooms */}
          {property.bathrooms != null &&
            (property.bathrooms as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Bath className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Bathrooms</span>
                <span className="font-medium ml-2">{property.bathrooms}</span>
              </div>
            )}

          {/* Number of Floors */}
          {property.num_floors != null &&
            (property.num_floors as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Number of Floors</span>
                <span className="font-medium ml-2">{property.num_floors}</span>
              </div>
            )}

          {/* Year Built */}
          {property.year_built != null &&
            (property.year_built as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Hammer className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Year Built</span>
                <span className="font-medium ml-2">{property.year_built}</span>
              </div>
            )}

          {/* Availability Status */}
          {property.availability_status != null &&
            property.availability_status !== "NULL" && (
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Availability Status</span>
                <span className="font-medium ml-2">
                  {property.availability_status}
                </span>
              </div>
            )}

          {/* Advance Payment */}
          {property.advance_payment_months != null &&
            (property.advance_payment_months as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Advance Payment</span>
                <span className="font-medium ml-2">
                  {property.advance_payment_months} Months
                </span>
              </div>
            )}

          {/* Minimum Term */}
          {property.minimum_term_months != null &&
            (property.minimum_term_months as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Hourglass className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Minimum Term</span>
                <span className="font-medium ml-2">
                  {property.minimum_term_months}{" "}
                  {property.minimum_term_unit !== "NULL"
                    ? property.minimum_term_unit
                    : "months"}
                </span>
              </div>
            )}

          {/* Bills Included */}
          {property.bills_included != null &&
            (property.bills_included as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                {property.bills_included ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <X className="h-5 w-5 text-red-600" />
                )}
                <span className="text-gray-600">Bills Included</span>
                <span className="font-medium ml-2">
                  {property.bills_included ? "Yes" : "No"}
                </span>
              </div>
            )}

          {/* Parking */}
          {property.parking_spaces != null &&
            (property.parking_spaces as any) !== "NULL" && (
              <div className="flex items-center gap-2">
                <Car className="h-5 w-5 text-gray-600" />
                <span className="text-gray-600">Parking Spaces</span>
                <span className="font-medium ml-2">
                  {property.parking_spaces}
                </span>
              </div>
            )}

          {/* Listed On */}
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-600" />
            <span className="text-gray-600">Listed:</span>
            <span className="font-medium ml-2">{listingDate}</span>
          </div>
        </div>

        {/* Attributes */}
        {normalizedAttributes && normalizedAttributes.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-medium text-propertylk-darkblue mb-3">
              Attributes
            </h4>
            <div className="flex flex-wrap gap-2">
              {normalizedAttributes.map((attribute) => (
                <Badge
                  key={attribute.id}
                  variant="outline"
                  className="bg-propertylk-lightblue text-propertylk-blue border-propertylk-blue/20"
                >
                  {attribute.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card>
      <LocationDetailsSection property={property} />
    </>
  );
};

export default PropertyDetailsSection;
